import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface GooglePlaceReview {
  authorName: string;
  rating: number;
  text: string;
  time: number;
  relativeTime: string;
}

interface GooglePlaceData {
  rating: number | null;
  reviewCount: number;
  reviews: GooglePlaceReview[];
  googleUrl: string | null;
}

interface UseGooglePlacesProps {
  siteName: string;
  address: string;
  city: string;
}

export const useGooglePlaces = ({ siteName, address, city }: UseGooglePlacesProps) => {
  const [data, setData] = useState<GooglePlaceData>({
    rating: null,
    reviewCount: 0,
    reviews: [],
    googleUrl: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlaceData = async () => {
      if (!siteName || !address || !city) {
        console.log('useGooglePlaces: Missing required params', { siteName, address, city });
        return;
      }

      console.log('useGooglePlaces: Starting fetch for', { siteName, address, city });
      setLoading(true);
      setError(null);

      try {
        console.log('useGooglePlaces: Calling supabase function...');
        const { data: result, error: functionError } = await supabase.functions.invoke('google-places-details', {
          body: { siteName, address, city }
        });

        console.log('useGooglePlaces: Function response', { result, functionError });

        if (functionError) {
          console.error('useGooglePlaces: Function error', functionError);
          throw functionError;
        }

        console.log('useGooglePlaces: Setting data', result);
        setData(result);
      } catch (err) {
        console.error('Error fetching Google Places data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchPlaceData();
  }, [siteName, address, city]);

  return { data, loading, error };
};